<script lang="ts">
	import Formatted from "@components/common/Formatted.svelte";
	import Heading from "@components/common/Heading.svelte";
	import Layout from "@components/layout/Layout.svelte";
	import { getTranslate } from "@utils/getTranslate.js";
	import { initScrollSpy } from "@utils/initScrollSpy.js";
	import { getContactPath } from "@core/schema/paths/getContactPath.js";
	import { getMembershipPagePath } from "@core/schema/paths/getMembershipPagePath.js";
	import translations from "./DeliveryAndPayment.translations.json" with { type: "json" };
	import type { DeliveryAndPaymentProps } from "./DeliveryAndPaymentProps.js";

	const { tenant }: DeliveryAndPaymentProps = $props();
	const translate = getTranslate(translations);

	const sections = [
		{ id: "roasting", key: "roastingHeading" },
		{ id: "delivery", key: "deliveryHeading" },
		{ id: "free-delivery", key: "freeDeliveryHeading" },
		{ id: "payment", key: "paymentHeading" },
		{ id: "subscription", key: "subscriptionHeading" },
		{ id: "notes", key: "notesHeading" },
		{ id: "contact", key: "contactHeading" },
	] as const;

	let activeId: string = $state(sections[0].id);
</script>

<Layout>
	<div
		class="tos-layout container-10"
		use:initScrollSpy={{
			sections,
			setActiveId: (id): void => {
				activeId = id;
			},
		}}
	>
		<nav class="tos-nav">
			<div class="sticky top-28">
				<p class="text-xs font-semibold uppercase tracking-wider text-fcrdarkgray/40 mb-4 pl-4">Obsah</p>
				<ul class="text-sm leading-relaxed">
					{#each sections as section (section.id)}
						<li>
							<a
								href="#{section.id}"
								class="block py-1.5 transition-colors duration-200 border-l-2 pl-4 {activeId === section.id
									? 'border-fcrdarkblue text-fcrdarkblue font-semibold'
									: 'border-fcrdarkgray/10 text-fcrdarkgray/40 hover:text-fcrdarkgray/70 hover:border-fcrdarkgray/30'}"
							>
								<Formatted text={translate(section.key)} />
							</a>
						</li>
					{/each}
				</ul>
			</div>
		</nav>

		<section class="tos-content font-medium">
			<Heading level={1} text={translate("heading")} class="heading-1 mb-10" />

			<!-- Roasting & Dispatch -->
			<Heading level={2} text={translate("roastingHeading")} id="roasting" class="heading-2 mt-16 mb-6" />
			<p class="mb-4"><Formatted text={translate("roastingText")} /></p>
			<p class="font-semibold mb-3"><Formatted text={translate("roastingScheduleHeading")} /></p>
			<ul class="list-disc pl-6 mb-4 space-y-1">
				<li><Formatted text={translate("scheduleMonday")} /></li>
				<li><Formatted text={translate("scheduleTuesday")} /></li>
				<li><Formatted text={translate("scheduleWednesday")} /></li>
				<li><Formatted text={translate("scheduleWeekend")} /></li>
			</ul>

			<!-- Delivery options -->
			<Heading level={2} text={translate("deliveryHeading")} id="delivery" class="heading-2 mt-16 mb-6" />

			<Heading level={3} text={translate("deliveryPplAddress")} class="font-antipol text-xl mt-8 mb-3" />
			<p class="mb-4"><Formatted text={translate("deliveryPplAddressText")} /></p>

			<Heading level={3} text={translate("deliveryPplShop")} class="font-antipol text-xl mt-8 mb-3" />
			<p class="mb-4"><Formatted text={translate("deliveryPplShopText")} /></p>

			<Heading level={3} text={translate("deliveryPplBox")} class="font-antipol text-xl mt-8 mb-3" />
			<p class="mb-4"><Formatted text={translate("deliveryPplBoxText")} /></p>

			<Heading level={3} text={translate("deliveryPickupOstrava")} class="font-antipol text-xl mt-8 mb-3" />
			<p class="mb-4"><Formatted text={translate("deliveryPickupOstravaText")} /></p>

			<Heading level={3} text={translate("deliveryPickupPrague")} class="font-antipol text-xl mt-8 mb-3" />
			<p class="mb-4"><Formatted text={translate("deliveryPickupPragueText")} /></p>

			<!-- Free delivery -->
			<Heading level={2} text={translate("freeDeliveryHeading")} id="free-delivery" class="heading-2 mt-16 mb-6" />
			<p class="mb-4"><Formatted text={translate("freeDeliveryB2cText")} /></p>
			<p>
				<a
					href={getMembershipPagePath(tenant)}
					class="underline underline-offset-4 hover:text-fcrdarkblue transition-colors"
				>
					<Formatted text={translate("freeDeliveryMembershipLink")} />
				</a>
			</p>

			<!-- Payment options -->
			<Heading level={2} text={translate("paymentHeading")} id="payment" class="heading-2 mt-16 mb-6" />

			<Heading level={3} text={translate("paymentOnline")} class="font-antipol text-xl mt-8 mb-3" />
			<p class="mb-3"><Formatted text={translate("paymentOnlineText")} /></p>
			<ul class="list-disc pl-6 mb-4 space-y-1">
				<li><Formatted text={translate("paymentCard")} /></li>
				<li><Formatted text={translate("paymentGPay")} /></li>
				<li><Formatted text={translate("paymentApplePay")} /></li>
				<li><Formatted text={translate("paymentQr")} /></li>
			</ul>

			<Heading level={3} text={translate("paymentOnDelivery")} class="font-antipol text-xl mt-8 mb-3" />
			<p class="mb-4"><Formatted text={translate("paymentOnDeliveryText")} /></p>

			<Heading level={3} text={translate("paymentOnInvoice")} class="font-antipol text-xl mt-8 mb-3" />
			<p class="mb-4"><Formatted text={translate("paymentOnInvoiceText")} /></p>

			<Heading level={3} text={translate("paymentPersonalPickup")} class="font-antipol text-xl mt-8 mb-3" />
			<p class="mb-4"><Formatted text={translate("paymentPersonalPickupText")} /></p>

			<!-- Subscription -->
			<Heading level={2} text={translate("subscriptionHeading")} id="subscription" class="heading-2 mt-16 mb-6" />
			<p class="mb-4"><Formatted text={translate("subscriptionText")} /></p>

			<!-- Notes -->
			<Heading level={2} text={translate("notesHeading")} id="notes" class="heading-2 mt-16 mb-6" />
			<ul class="list-disc pl-6 mb-4 space-y-2">
				<li><Formatted text={translate("notePackaging")} /></li>
				<li><Formatted text={translate("notePaymentTiming")} /></li>
			</ul>

			<!-- Contact -->
			<Heading level={2} text={translate("contactHeading")} id="contact" class="heading-2 mt-16 mb-6" />
			<p class="mb-4"><Formatted text={translate("contactText")} /></p>
			<p>
				<a href={getContactPath(tenant)} class="underline underline-offset-4 hover:text-fcrdarkblue transition-colors">
					<Formatted text={translate("contactLinkLabel")} />
				</a>
			</p>
		</section>
	</div>
</Layout>

<style lang="postcss">
	.tos-layout {
		@apply flex gap-12 py-10 lg:gap-20 lg:py-16;
	}

	.tos-nav {
		@apply hidden shrink-0 lg:block;
		width: 240px;
	}

	.tos-content {
		@apply min-w-0 max-w-[55ch] text-base leading-relaxed;
	}

	.tos-content :global(h2) {
		scroll-margin-top: 7rem;
	}

	.tos-content :global(h3) {
		scroll-margin-top: 7rem;
	}
</style>
